/**
 * NationalMapPage — `/franchise/national-map`.
 *
 * Wraps `GET /v1/franchise/dashboard/central/map` (state / tier / status
 * rollups) and `GET /v1/franchise/ops/coordinates/pins` (per-franchise lat/lng
 * pins persisted via `PUT /v1/franchise/ops/coordinates/franchise/:id`).
 *
 * The map renders state-level markers from the rollup PLUS individual
 * franchise pins for any outlet whose coordinates have been captured. The
 * by-state / by-tier / by-status tables remain unchanged.
 */
import { type JSX, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import { getCentralMap, listFranchisePins } from '../../lib/api/franchise';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';
import { UpmpMap, type UpmpMapMarker } from '../shared/UpmpMap';
import { KPICard } from '../mail/shared/MailUI';
import { NIGERIA_CENTER, STATE_CENTROIDS } from './state-centroids';

export function NationalMapPage(): JSX.Element {
  const map = useQuery({
    queryKey: ['franchise', 'national-map'],
    queryFn: () => getCentralMap(),
    retry: false,
  });

  const pins = useQuery({
    queryKey: ['franchise', 'national-map', 'pins'],
    queryFn: () => listFranchisePins(),
    retry: false,
  });

  const [highlightedState, setHighlightedState] = useState<string | null>(null);
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});

  const denied =
    map.isError && map.error instanceof ApiError &&
    (map.error.status === 401 || map.error.status === 403);

  const markers = useMemo<readonly UpmpMapMarker[]>(() => {
    const out: UpmpMapMarker[] = [];
    if (map.data) {
      for (const s of map.data.byState) {
        const centroid = STATE_CENTROIDS[s.state];
        if (!centroid) continue;
        out.push({
          id: `state-${s.state}`,
          position: centroid,
          title: `${s.state} — ${s.count} franchise${s.count === 1 ? '' : 's'}`,
          label: String(s.count),
          onClick: () => {
            setHighlightedState(s.state);
            const row = rowRefs.current[s.state];
            if (row) {
              row.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          },
        } satisfies UpmpMapMarker);
      }
    }
    if (pins.data?.items) {
      for (const p of pins.data.items) {
        const lat = Number.parseFloat(p.latitude);
        const lng = Number.parseFloat(p.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
        out.push({
          id: `pin-${p.franchiseId}`,
          position: { lat, lng },
          title: `${p.legalName} (${p.franchiseNumber}) — ${p.tierCode} · ${p.status}`,
        } satisfies UpmpMapMarker);
      }
    }
    return out;
  }, [map.data, pins.data]);

  const pinCount = pins.data?.items.length ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="National map"
        description="Network footprint by state, tier and status."
      />

      {map.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          <code className="font-mono">FranchiseDashboard/read</code> required.
        </DeferredNotice>
      ) : map.isError ? (
        <DeferredNotice title="Service unreachable">{(map.error as Error).message}</DeferredNotice>
      ) : map.data ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <KPICard title="Live franchises" value={String(map.data.totalLive)} />
            <KPICard title="States covered" value={String(map.data.byState.length)} />
            <KPICard title="Tiers" value={String(map.data.byTier.length)} />
            <KPICard
              title="Pinned outlets"
              value={String(pinCount)}
              hint="Franchises with lat/lng coordinates set"
            />
          </div>

          <div className="bg-white rounded-xl border border-border overflow-hidden">
            <div className="px-5 py-4 border-b border-border">
              <h3 className="font-semibold text-gray-900">National coverage</h3>
              <p className="text-xs text-muted mt-0.5">
                Marker label = franchise count for that state. Click a pin to jump to its row.
              </p>
            </div>
            <UpmpMap center={NIGERIA_CENTER} zoom={6} markers={markers} height={420} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border border-border rounded-xl overflow-hidden">
              <p className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                By state
              </p>
              <table className="w-full text-sm">
                <tbody className="divide-y divide-border">
                  {map.data.byState.map((s) => (
                    <tr
                      key={s.state}
                      ref={(el) => {
                        rowRefs.current[s.state] = el;
                      }}
                      className={
                        highlightedState === s.state
                          ? 'bg-nipost-green/10'
                          : 'hover:bg-gray-50'
                      }
                    >
                      <td className="px-4 py-2 text-xs text-gray-700">{s.state}</td>
                      <td className="px-4 py-2 text-right text-xs font-mono">{s.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="space-y-6">
              <div className="bg-white border border-border rounded-xl overflow-hidden">
                <p className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  By tier
                </p>
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-border">
                    {map.data.byTier.map((t) => (
                      <tr key={t.tier} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-xs text-gray-700">{t.tier}</td>
                        <td className="px-4 py-2 text-right text-xs font-mono">{t.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-white border border-border rounded-xl overflow-hidden">
                <p className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  By status
                </p>
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-border">
                    {map.data.byStatus.map((s) => (
                      <tr key={s.status} className="hover:bg-gray-50">
                        <td className="px-4 py-2">
                          <StatusBadge status={s.status} />
                        </td>
                        <td className="px-4 py-2 text-right text-xs font-mono">{s.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
